import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { DollarSign, Receipt, TrendingUp, Users, Megaphone, Printer, BarChart3 } from "lucide-react";
import { LineChart, Line, BarChart, Bar, CartesianGrid, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer } from "recharts";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import StatCard from "@/components/StatCard";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

const fmt = (n) => new Intl.NumberFormat("ar-SA", { maximumFractionDigits: 2 }).format(n || 0);
const currency = (n) => `${fmt(n)} ر.س`;

export default function CommercialDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [chart, setChart] = useState([]);
  const [period, setPeriod] = useState("monthly");

  const load = async () => {
    try {
      const [s, c] = await Promise.all([
        api.get("/stats/commercial"),
        api.get("/stats/commercial/chart", { params: { period } }),
      ]);
      setStats(s.data);
      setChart(c.data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  useEffect(() => { load(); }, [period]);

  return (
    <div className="p-4 sm:p-6 lg:p-10">
      <PageHeader
        title="لوحة تحكم البوابة التجارية"
        subtitle="نظرة شاملة على الإيرادات والمصروفات والحملات"
        actions={
          <Button
            data-testid="dashboard-print-btn"
            variant="outline"
            onClick={() => window.open("/print/commercial/dashboard", "_blank")}
            className="border-emerald-900 text-emerald-900 hover:bg-emerald-50"
          >
            <Printer className="h-4 w-4 ml-2" />
            طباعة
          </Button>
        }
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4 mb-6 sm:mb-8">
        <StatCard testId="stat-revenue" icon={DollarSign} label="إجمالي الإيرادات" value={currency(stats?.revenue)} tone="emerald" />
        <StatCard testId="stat-expenses" icon={Receipt} label="إجمالي المصروفات" value={currency(stats?.expenses)} tone="gold" />
        <StatCard testId="stat-net" icon={TrendingUp} label="صافي الدخل" value={currency(stats?.net_income)}
          tone={(stats?.net_income || 0) >= 0 ? "white" : "danger"} />
        <StatCard testId="stat-beneficiaries" icon={Users} label="المستفيدون" value={fmt(stats?.beneficiaries_count)} tone="white" />
        <StatCard testId="stat-campaigns" icon={Megaphone} label="الحملات" value={fmt(stats?.campaigns_count)} tone="white" />
      </div>

      <Card className="p-6 border-emerald-900/10">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-3">
          <div>
            <h3 className="font-heading text-xl font-bold text-emerald-950 flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-amber-600" />
              الأداء المالي عبر الزمن
            </h3>
            <p className="text-sm text-slate-500 mt-1">إيرادات مقابل مصروفات مع صافي الدخل</p>
          </div>
          <Tabs value={period} onValueChange={setPeriod}>
            <TabsList data-testid="chart-period-tabs">
              <TabsTrigger value="weekly" data-testid="period-weekly">أسبوعي</TabsTrigger>
              <TabsTrigger value="monthly" data-testid="period-monthly">شهري</TabsTrigger>
              <TabsTrigger value="yearly" data-testid="period-yearly">سنوي</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        <div className="h-80" dir="ltr">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chart}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis dataKey="period" stroke="#4B5563" style={{ fontSize: 12 }} />
              <YAxis stroke="#4B5563" style={{ fontSize: 12 }} />
              <Tooltip
                contentStyle={{ direction: "rtl", background: "#fff", border: "1px solid #E5E7EB", borderRadius: 8 }}
                formatter={(v, k) => [currency(v), k === "revenue" ? "إيرادات" : k === "expenses" ? "مصروفات" : "صافي"]}
              />
              <Legend formatter={(v) => (v === "revenue" ? "إيرادات" : v === "expenses" ? "مصروفات" : "صافي")} />
              <Bar dataKey="revenue" fill="#064E3B" radius={[6, 6, 0, 0]} />
              <Bar dataKey="expenses" fill="#D97706" radius={[6, 6, 0, 0]} />
              <Bar dataKey="net" fill="#10B981" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <div className="grid md:grid-cols-3 gap-4 mt-8">
        <button onClick={() => navigate("beneficiaries")} className="p-6 bg-white rounded-2xl border border-emerald-900/10 text-right hover:-translate-y-0.5 hover:shadow-lg transition-all">
          <Users className="h-6 w-6 text-emerald-900 mb-3" />
          <div className="font-heading font-bold text-emerald-950">إدارة المستفيدين</div>
          <div className="text-sm text-slate-500 mt-1">تسجيل وبحث وتعديل بيانات المشتركين</div>
        </button>
        <button onClick={() => navigate("expenses")} className="p-6 bg-white rounded-2xl border border-emerald-900/10 text-right hover:-translate-y-0.5 hover:shadow-lg transition-all">
          <Receipt className="h-6 w-6 text-amber-600 mb-3" />
          <div className="font-heading font-bold text-emerald-950">تسجيل المصروفات</div>
          <div className="text-sm text-slate-500 mt-1">فندق، باص، مشرف، جوائز، أخرى</div>
        </button>
        <button onClick={() => navigate("income")} className="p-6 bg-white rounded-2xl border border-emerald-900/10 text-right hover:-translate-y-0.5 hover:shadow-lg transition-all">
          <TrendingUp className="h-6 w-6 text-emerald-700 mb-3" />
          <div className="font-heading font-bold text-emerald-950">قائمة الدخل</div>
          <div className="text-sm text-slate-500 mt-1">حسابات صافي الدخل حسب الحملة</div>
        </button>
      </div>
    </div>
  );
}
